import React, { useState, useCallback, useEffect, useContext, useMemo, useRef } from 'react';
import styled from 'styled-components';
import RGL, { ItemCallback, Layout, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import CustomReportModalAddNew from './CustomReportModal_AddNew';
import CustomReportText from './CustomReportText';
// import { sectionGridLayout } from './CustomReportTab';
import CustomReportFile from './CustomReportFile';
import CustomReportKPI, { KPIvalues } from './CustomReportKPI';
import { GridLayout, MetricsResponse, SectionGridLayout, UserQuery } from 'API';
import { useCustomReportContext } from 'context/CustomReportContext';
import {
  useDeleteSectionItem,
  useUpdateSectionItem,
  useGetCustomReportPublishedData,
} from './CustomReportsHooks/useCustomReportsGrid';
import { useGetStoredFile } from './CustomReportsHooks/useCustomReportsFile';
import { useGetKPIValues } from './CustomReportsHooks/useCustomReportsKPI';
import { useGetSavedText } from './CustomReportsHooks/useCustomReportsText';
import CustomReportQuery from './CustomReportQuery';
import { useGetStoredQuery } from './CustomReportsHooks/useCustomReportsQueries';
import { v4 as uuidv4 } from 'uuid';
import APIContext from 'context/APIContext';
import { getFilesFromS3 } from '../Main/Brief/UploadFile/getFilesfromS3';
import CustomReportCreative, { CreativeValues } from './CustomReportCreative';
import { useGetStoredCreative } from './CustomReportsHooks/useCustomReportsCreative';
const ReactGridLayout = WidthProvider(RGL);

// type ItemCallback = (layout: Layout, oldItem: Layout, newItem: Layout,
//   placeholder: Layout, e: MouseEvent, element: HTMLElement)

const calcHeight = () => {
  return `${8 * 80 + 11 * 7}px`;
};

// export type gridLayout = {
//   position: string;
//   gridPosition: Layout;
//   type: string | null;
//   hasData: boolean;
//   open: boolean;
// };

type PlaceholderGridProps = {
  displayGrid: boolean;
};

const PlaceholderGrid = styled.div<PlaceholderGridProps>`
  display: ${(props) => (props.displayGrid ? 'grid' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  /* display: grid; */
  grid-template-columns: repeat(8, 1fr);
  grid-template-rows: repeat(8, 1fr);
  width: 100%;
  gap: 11px;
  padding: 10px;

  .grid-item {
    background: linear-gradient(135deg, #ccc 0%, #c0c0c0 100%);
    border-radius: 8px;
    height: 80px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 2rem;
    color: white;
    font-weight: bold;
    /* box-shadow:
      0 1px 4px rgba(0, 0, 0, 0.06),
      0 1px 2px rgba(0, 0, 0, 0.04); */
    /* border: 1px solid rgba(255, 255, 255, 0.2); */
    transition: all 0.2s ease;
    cursor: pointer;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
    }

    &:hover {
      transform: scale(1.02);
      box-shadow:
        0 2px 6px rgba(0, 0, 0, 0.08),
        0 1px 3px rgba(0, 0, 0, 0.05);
      /* background: linear-gradient(135deg, #d4d4d4 0%, #c8c8c8 100%); */
    }
  }
`;

const AddElementMenu = styled.div<{ $positionRight?: boolean; $positionTop?: boolean }>`
  position: absolute;
  top: ${(props) => (props.$positionTop ? 'auto' : '0')};
  bottom: ${(props) => (props.$positionTop ? '0' : 'auto')};
  left: ${(props) => (props.$positionRight ? 'auto' : 'calc(100% + 8px)')};
  right: ${(props) => (props.$positionRight ? 'calc(100% + 8px)' : 'auto')};
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  z-index: 9999;
  min-width: 140px;
  animation: fadeInSlide 0.1s ease-out;

  @keyframes fadeInSlide {
    from {
      opacity: 0;
      transform: translateX(${(props) => (props.$positionRight ? '4px' : '-4px')});
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .menu-items {
    padding: 4px;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    cursor: pointer;
    transition: background 0.1s ease;
    color: #333;
    border-radius: 4px;
    white-space: nowrap;

    &:hover {
      background: #f5f5f5;
    }

    .material-symbols-outlined {
      font-size: 18px;
      color: #555;
    }

    span:not(.material-symbols-outlined) {
      font-size: 13px;
      font-weight: 500;
    }
  }
`;

type Props = {
  sectionDetails: SectionGridLayout;
  isSectionDraggable: boolean;
  displayGrid: boolean;
  disableModal?: boolean;
};

export type queryValues = {
  query_id?: string | null;
  query_id_cc?: string | null;
  query_id_benchmark?: string | null;
  title?: string | null;
  metrics?: Array<MetricsResponse | null> | null;
  hideTotal?: boolean | null;
};

const CustomReportSection = (props: Props) => {
  const {
    sectionLayoutState,
    safeSectionLayoutUpdate,
    setPublishPlaceholderObject,
    activeCustomReport,
    registerCleanupFunction,
    unregisterCleanupFunction,
  } = useCustomReportContext();
  const userIsAuthorized = activeCustomReport?.authorized;
  const [deleteSectionItem] = useDeleteSectionItem();
  const { isSectionDraggable, sectionDetails, displayGrid, disableModal } = props;
  const columns = 8;
  const rows = 8;
  const [updateSectionItem] = useUpdateSectionItem();
  const [updateInProgress, setUpdateInProgress] = useState(false);

  // Add cleanup function to remove duplicates and fix corrupted data
  const cleanupSectionElements = useCallback(
    (elements: Array<GridLayout | null>) => {
      if (!elements || elements.length === 0) return [];

      // Remove null elements
      const nonNullElements = elements.filter((el): el is GridLayout => el !== null);

      // Remove duplicates based on GridPosition.i and prioritize elements with SectionItemID
      const uniqueElements = new Map<string, GridLayout>();
      const seenPositions = new Set<string>();
      const seenSectionItemIds = new Set<string>();

      // Sort to prioritize elements with SectionItemID
      const sortedElements = [...nonNullElements].sort((a, b) => {
        if (a.SectionItemID && !b.SectionItemID) return -1;
        if (!a.SectionItemID && b.SectionItemID) return 1;
        return 0;
      });

      for (const element of sortedElements) {
        if (!element.GridPosition?.i) continue;

        const sectionItemId = element.SectionItemID;
        if (sectionItemId && seenSectionItemIds.has(sectionItemId)) {
          continue;
        }

        const positionKey = `${element.GridPosition.x}-${element.GridPosition.y}`;
        const gridId = element.GridPosition.i;

        // Skip if we already have this grid ID and the existing one has SectionItemID
        if (uniqueElements.has(gridId)) {
          const existing = uniqueElements.get(gridId);
          if (existing?.SectionItemID && !element.SectionItemID) {
            continue; // Keep the existing one with SectionItemID
          }
        }

        // Skip if position is already occupied by an element with SectionItemID
        if (seenPositions.has(positionKey)) {
          const existingAtPosition = Array.from(uniqueElements.values()).find(
            (el) => el.GridPosition && `${el.GridPosition.x}-${el.GridPosition.y}` === positionKey,
          );
          if (existingAtPosition?.SectionItemID && !element.SectionItemID) {
            continue;
          }
        }

        // Validate grid position bounds
        if (
          element.GridPosition?.x !== null &&
          element.GridPosition?.x !== undefined &&
          element.GridPosition.x >= 0 &&
          element.GridPosition?.x < columns &&
          element.GridPosition?.y !== null &&
          element.GridPosition?.y !== undefined &&
          element.GridPosition.y >= 0 &&
          element.GridPosition?.y < rows &&
          element.GridPosition?.w !== null &&
          element.GridPosition?.w !== undefined &&
          element.GridPosition?.h !== null &&
          element.GridPosition?.h !== undefined &&
          element.GridPosition.x + element.GridPosition.w <= columns &&
          element.GridPosition.y + element.GridPosition.h <= rows
        ) {
          uniqueElements.set(gridId, element);
          seenPositions.add(positionKey);
          if (sectionItemId) {
            seenSectionItemIds.add(sectionItemId);
          }
        }
      }

      return Array.from(uniqueElements.values()).slice(0, 64); // Enforce max 64 elements
    },
    [columns, rows, sectionDetails.SectionID],
  );
  const currentSection =
    sectionLayoutState.find((section) => section.SectionID === sectionDetails.SectionID) || sectionDetails;

  // Clean up section elements to prevent duplicates and corruption
  const cleanedCurrentSection = useMemo(
    () => ({
      ...currentSection,
      SectionElements: cleanupSectionElements(currentSection.SectionElements || []),
    }),
    [currentSection, cleanupSectionElements],
  );

  // Auto-cleanup corrupted sections on mount
  useEffect(() => {
    const originalElements = currentSection.SectionElements || [];
    const cleanedElements = cleanupSectionElements(originalElements);

    if (
      originalElements.length !== cleanedElements.length ||
      originalElements.some((orig, index) => orig?.GridPosition?.i !== cleanedElements[index]?.GridPosition?.i)
    ) {
      console.warn(
        `Cleaning up corrupted section ${currentSection.SectionID}: ${originalElements.length} -> ${cleanedElements.length} elements`,
      );

      safeSectionLayoutUpdate((prevLayout) =>
        prevLayout.map((section) => {
          if (section.SectionID === currentSection.SectionID) {
            return {
              ...section,
              SectionElements: cleanedElements,
            };
          }
          return section;
        }),
      );
    }
  }, [currentSection.SectionID, currentSection.SectionElements, cleanupSectionElements, safeSectionLayoutUpdate]);

  const openedItem = cleanedCurrentSection.SectionElements?.find((item) => item?.Open);

  // Find any unsaved item (item without SectionItemID) for click-outside detection
  const unsavedItem = cleanedCurrentSection.SectionElements?.find((item) => !item?.SectionItemID);
  const menuRef = useRef<HTMLDivElement>(null);

  // Click outside to close unsaved menu (but not when a modal is open)
  useEffect(() => {
    // Don't add listener if there's no unsaved item OR if a modal is currently open
    if (!unsavedItem || openedItem) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside the menu
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        safeSectionLayoutUpdate((prevLayout) =>
          prevLayout.map((section) => {
            if (section.SectionID === cleanedCurrentSection.SectionID) {
              const filteredElements = (section.SectionElements ?? []).filter(
                (el) => el?.SectionItemID !== null && el?.SectionItemID !== undefined,
              );
              return {
                ...section,
                SectionElements: filteredElements,
              };
            }
            return section;
          }),
        );
      }
    };

    // Small delay to add listener, so initial click doesn't trigger it
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [unsavedItem, openedItem, cleanedCurrentSection.SectionID, safeSectionLayoutUpdate]);

  const gridChildrenObjects = Array.from({ length: columns * rows }, (_, index) => ({
    i: `${index + 1}`,
    x: (index % columns) * 1,
    y: Math.floor(index / columns) * 1,
    w: 1,
    h: 1,
  }));
  const generateId = useCallback(() => uuidv4(), []);
  const gridItems = gridChildrenObjects.map((item) => (
    <div
      key={item.i}
      className="grid-item"
      onClick={() => {
        if (isSectionDraggable) return;

        // Enhanced validation to prevent duplicates
        const currentElements = cleanedCurrentSection.SectionElements || [];

        // Check if position is already occupied
        const positionOccupied = currentElements.some(
          (el) => el?.GridPosition?.x === item.x && el?.GridPosition?.y === item.y,
        );
        if (positionOccupied) return;

        // Check if we're at maximum capacity (64 elements for 8x8 grid)
        if (currentElements.length >= 64) {
          alert('Maximum number of elements reached (64). Please delete some elements before adding new ones.');
          return;
        }

        // Generate unique ID and ensure it doesn't already exist
        const findUniqueId = (existingElements: Array<GridLayout | null>): string => {
          for (let attempts = 0; attempts < 10; attempts++) {
            const newId = generateId();
            if (!existingElements.some((el) => el?.GridPosition?.i === newId)) {
              return newId;
            }
          }
          throw new Error('Failed to generate unique ID after 10 attempts');
        };

        let uniqueId: string;
        try {
          uniqueId = findUniqueId(currentElements);
        } catch (error) {
          console.error(error);
          return;
        }

        safeSectionLayoutUpdate((prevLayout) =>
          prevLayout.map((section) => {
            if (section.SectionID === sectionDetails.SectionID) {
              // First, remove any existing unsaved items (those without SectionItemID)
              const cleanedElements = (section.SectionElements ?? []).filter(
                (el) => el?.SectionItemID !== null && el?.SectionItemID !== undefined,
              );

              const updatedElements = [
                ...cleanedElements,
                {
                  __typename: 'GridLayout',
                  SectionItemID: null,
                  Position: cleanedElements.length + 1,
                  GridPosition: { ...item, i: uniqueId, __typename: 'Layout' },
                  Type: null,
                  HasData: false,
                  Open: false,
                } as GridLayout,
              ];

              return {
                ...section,
                SectionElements: updatedElements,
              };
            }
            return section;
          }),
        );
      }}
    >
      +
    </div>
  ));

  const handleUpdateLayout: ItemCallback = useCallback(
    (layout, oldItem, newItem, placeholder, event, element) => {
      // Prevent race conditions
      if (updateInProgress) return;
      setUpdateInProgress(true);

      try {
        // Use cleaned section elements
        const currentElements = cleanedCurrentSection.SectionElements || [];

        const updatesSectionDetails = {
          ...sectionDetails,
          SectionElements: currentElements.map((item) => {
            if (item?.GridPosition?.i === newItem.i) {
              return {
                ...item,
                GridPosition: {
                  ...item.GridPosition,
                  x: newItem.x,
                  y: newItem.y,
                  w: newItem.w,
                  h: newItem.h,
                },
              };
            }
            return item;
          }),
        };

        const findItem = updatesSectionDetails.SectionElements?.find((item) => item?.GridPosition?.i === newItem.i);
        const isSameGridPosition = (a: any, b: any) => {
          return a && b && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h && a.i === b.i;
        };

        if (isSameGridPosition(oldItem, newItem)) {
          setUpdateInProgress(false);
          return;
        }

        if (findItem?.SectionItemID) {
          updateSectionItem(sectionDetails.SectionID as string, findItem.SectionItemID, {
            Position: Number(newItem.i),
            GridPosition: {
              i: findItem.GridPosition?.i,
              x: findItem.GridPosition?.x,
              y: findItem.GridPosition?.y,
              w: findItem.GridPosition?.w,
              h: findItem.GridPosition?.h,
            },
            Type: findItem.Type,
            HasData: findItem.HasData,
          }).then((response) => {
            if (response === 'success') {
              safeSectionLayoutUpdate((prevLayout) => {
                return prevLayout.map((section) => {
                  if (section.SectionID === sectionDetails.SectionID) {
                    return {
                      ...updatesSectionDetails,
                      SectionElements: updatesSectionDetails.SectionElements || [],
                    };
                  }
                  return section;
                });
              });
            }
          });
        } else {
          safeSectionLayoutUpdate((prevLayout) => {
            return prevLayout.map((section) => {
              if (section.SectionID === sectionDetails.SectionID) {
                return {
                  ...updatesSectionDetails,
                  SectionElements: updatesSectionDetails.SectionElements || [],
                };
              }
              return section;
            });
          });
        }
      } catch (error) {
        console.error('Error updating grid layout:', error);
      } finally {
        setUpdateInProgress(false);
      }
    },
    [sectionDetails, updateSectionItem, safeSectionLayoutUpdate, updateInProgress, cleanedCurrentSection],
  );
  const handleModalType = useCallback(
    (item: GridLayout | null, type: string | null) => {
      if (item) {
        safeSectionLayoutUpdate((prevLayout) => {
          return prevLayout.map((section) => {
            if (section.Position === sectionDetails.Position) {
              return {
                ...section,
                SectionElements: (section.SectionElements ?? []).map((layoutItem) => {
                  if (
                    layoutItem?.GridPosition &&
                    item?.GridPosition &&
                    layoutItem.GridPosition.i === item.GridPosition.i
                  ) {
                    return {
                      ...layoutItem,
                      Type: type,
                      Open: true,
                      __typename: 'GridLayout',
                    } as GridLayout;
                  }
                  return layoutItem ? { ...layoutItem, __typename: 'GridLayout' } : layoutItem;
                }),
              };
            }
            return section;
          });
        });
      } else {
        const newSectionDetails = {
          ...sectionDetails,
          SectionElements: (sectionDetails.SectionElements ?? []).map((layoutItem) => {
            if (layoutItem?.Open) {
              return { ...layoutItem, Open: false, Type: layoutItem.SectionItemID ? layoutItem.Type : null };
            }
            return layoutItem;
          }),
        };

        safeSectionLayoutUpdate((prevLayout) => {
          const updatedLayout = prevLayout.map((section) => {
            if (section.Position === sectionDetails.Position) {
              return newSectionDetails;
            }
            return section;
          });
          return updatedLayout;
        });
      }
    },
    [safeSectionLayoutUpdate, sectionDetails],
  );

  const deleteItem = useCallback(
    (sectionID: string, sectionItemID: string) => {
      deleteSectionItem(sectionID, sectionItemID).then((response) => {
        if (response === 'success') {
          safeSectionLayoutUpdate((prevLayout) =>
            prevLayout.map((section) => {
              if (section.SectionID === sectionID) {
                return {
                  ...section,
                  SectionElements: (section.SectionElements ?? []).filter(
                    (item) => item?.SectionItemID !== sectionItemID,
                  ),
                };
              }
              return section;
            }),
          );
          setPublishPlaceholderObject((prev) => {
            const newState = { ...prev };
            delete newState[sectionItemID];
            return newState;
          });
        } else {
          alert(response);
        }
      });

      // setSectionLayoutState((prevLayout) =>
      //   prevLayout.map((section) => {
      //     if (section.Position === sectionDetails.Position) {
      //       return {
      //         ...section,
      //         sectionElements: (section.SectionElements ?? []).filter(
      //           (layoutItem) => layoutItem?.GridPosition?.i !== item.GridPosition?.i,
      //         ),
      //       };
      //     }
      //     return section;
      //   }),
      // );
    },
    [deleteSectionItem, safeSectionLayoutUpdate, setPublishPlaceholderObject],
  );

  // FETCH FILES
  const [fetchStoredFile] = useGetStoredFile();
  const [publishedData] = useGetCustomReportPublishedData();
  const [fileCache, setFileCache] = useState<Record<string, File | null>>({});
  const { getEnvironment } = useContext(APIContext);

  const fetchFile = useCallback(
    async (sectionItemID: string): Promise<File | null> => {
      if (!fileCache[sectionItemID]) {
        let file: File | null = null;

        if (activeCustomReport?.authorized) {
          // For authorized users, fetch from storage
          file = await fetchStoredFile(sectionItemID);
        } else {
          // For unauthorized users, fetch from published data
          try {
            const data = await publishedData(sectionItemID);
            // console.log('published data file', data);
            if (data && data.outputlocation) {
              const fakeUserQuery = {
                __typename: 'UserQuery',
                query_id: data.outputlocation,
                metrics: data.metrics,
              };

              const ff = await getFilesFromS3(
                fakeUserQuery as UserQuery,
                activeCustomReport?.Client?.Code as string,
                activeCustomReport?.Client?.Country as string,
                // activeCampaign?.ClientCode as string,
                // activeCampaign?.ClientCountry as string,
                getEnvironment,
              );
              if (ff) {
                file = ff as File;
              }
            }
          } catch (error) {
            console.error('Error loading file from published data:', error);
          }
        }

        setFileCache((prev) => ({ ...prev, [sectionItemID]: file }));
        return file;
      }
      return fileCache[sectionItemID] ?? null;
    },
    [
      fileCache,
      activeCustomReport?.authorized,
      fetchStoredFile,
      publishedData,
      activeCustomReport?.Client?.Code,
      activeCustomReport?.Client?.Country,
      getEnvironment,
    ],
  );

  const handleFileSaved = useCallback((sectionItemID: string, file: File) => {
    setFileCache((prev) => ({ ...prev, [sectionItemID]: file }));
  }, []);

  // FETCH KPI
  const [fetchCustomReportKPI] = useGetKPIValues();
  const [kpiCache, setKpiCache] = useState<Record<string, KPIvalues | null>>({});
  const fetchKPI = async (sectionItemID: string): Promise<KPIvalues | null> => {
    if (!kpiCache[sectionItemID]) {
      const kpiArr = await fetchCustomReportKPI(sectionItemID);
      const kpi = kpiArr?.[0]
        ? {
            labelTitle: kpiArr[0].label || '',
            labelSubtitle: kpiArr[0].subtitle || '',
            valueFormat: kpiArr[0].format || 'number',
            value: kpiArr[0].floatValue || 0,
          }
        : null;
      setKpiCache((prev) => ({ ...prev, [sectionItemID]: kpi }));
      return kpi;
    }
    return kpiCache[sectionItemID] ?? null;
  };

  const handleKpiSaved = (sectionItemID: string, kpi: KPIvalues) => {
    setKpiCache((prev) => ({ ...prev, [sectionItemID]: kpi }));
  };

  // FETCH TEXT
  const [getCustomReportText] = useGetSavedText();
  const [textCache, setTextCache] = useState<Record<string, string | null>>({});
  const fetchTextFromAPI = async (sectionItemID: string): Promise<string | null> => {
    const text = await getCustomReportText(sectionItemID);

    setTextCache((prev) => ({ ...prev, [sectionItemID]: text }));

    return text;
  };
  const handleTextSaved = (sectionItemID: string, text: string) => {
    setTextCache((prev) => ({ ...prev, [sectionItemID]: text }));
  };
  const showGrid = displayGrid && (userIsAuthorized as boolean);

  //FETCH QUERY
  const [getCustomReportQuery] = useGetStoredQuery();
  const [queryCache, setQueryCache] = useState<Record<string, queryValues | null>>({});

  const fetchQuery = async (sectionItemID: string): Promise<queryValues | null> => {
    if (!queryCache[sectionItemID]) {
      const query = await getCustomReportQuery(sectionItemID);
      setQueryCache((prev) => ({
        ...prev,
        [sectionItemID]: {
          query_id: query?.query_id ?? null,
          query_id_cc: query?.query_id_cc ?? null,
          query_id_benchmark: query?.query_id_benchmark ?? null,
          title: query?.title ?? null,
          metrics: query?.metrics ?? null,
          hideTotal: query?.hideTotal ?? false,
        },
      }));
      return query;
    }
    return queryCache[sectionItemID] ?? null;
  };
  const handleQuerySaved = (sectionItemID: string, query: queryValues) => {
    setQueryCache((prev) => ({ ...prev, [sectionItemID]: query }));
  };

  // FETCH CREATIVE
  const [getCustomReportCreative] = useGetStoredCreative();
  const [creativeCache, setCreativeCache] = useState<Record<string, CreativeValues | null>>({});

  const fetchCreative = async (sectionItemID: string): Promise<CreativeValues | null> => {
    if (!creativeCache[sectionItemID]) {
      const creativeData = await getCustomReportCreative(sectionItemID);
      if (creativeData) {
        const creativeValues: CreativeValues = {
          dataSourceName: creativeData.dataSourceName,
          creative: creativeData.creative,
          data: creativeData.data,
          outputLocation: creativeData.outputLocation,
          title: creativeData.title,
        };
        setCreativeCache((prev) => ({ ...prev, [sectionItemID]: creativeValues }));
        return creativeValues;
      }
      setCreativeCache((prev) => ({ ...prev, [sectionItemID]: null }));
      return null;
    }
    return creativeCache[sectionItemID] ?? null;
  };

  const [creativeCacheVersion, setCreativeCacheVersion] = useState(0);

  const handleCreativeSaved = (sectionItemID: string, creative: CreativeValues) => {
    setCreativeCache((prev) => ({
      ...prev,
      [sectionItemID]: creative,
    }));
    // Force re-render by incrementing version
    setCreativeCacheVersion((v) => v + 1);
  };

  // Register cleanup function to clear all caches when custom report is closed
  useEffect(() => {
    const sectionId = `section-${sectionDetails.SectionID}`;

    const cleanupSectionCaches = () => {
      setFileCache({});
      setKpiCache({});
      setTextCache({});
      setQueryCache({});
      setCreativeCache({});
      // console.log(`Cleared caches for section ${sectionDetails.SectionID}`);
    };

    registerCleanupFunction(sectionId, cleanupSectionCaches);

    // Cleanup when component unmounts
    return () => {
      unregisterCleanupFunction(sectionId);
    };
  }, [sectionDetails.SectionID, registerCleanupFunction, unregisterCleanupFunction]);

  return (
    <>
      <PlaceholderGrid displayGrid={showGrid}>{gridItems}</PlaceholderGrid>

      <ReactGridLayout
        className="layout"
        cols={columns}
        rowHeight={80}
        layout={(cleanedCurrentSection.SectionElements ?? []).map((item) => {
          return {
            i: item?.GridPosition?.i,
            x: item?.GridPosition?.x,
            y: item?.GridPosition?.y,
            w: item?.GridPosition?.w,
            h: item?.GridPosition?.h,
          } as Layout;
        })} // Convert gridLayout[] to Layout[]
        autoSize={true}
        isDraggable={!props.isSectionDraggable && userIsAuthorized}
        isResizable={!props.isSectionDraggable && userIsAuthorized}
        useCSSTransforms={true}
        margin={[11, 11]}
        containerPadding={[0, 0]}
        compactType={null}
        preventCollision={true}
        style={{ height: calcHeight(), pointerEvents: 'none' }}
        maxRows={rows}
        onResizeStop={handleUpdateLayout}
        onDragStop={handleUpdateLayout}
      >
        {(cleanedCurrentSection.SectionElements ?? []).map((item) => (
          <div
            style={{ pointerEvents: 'initial', overflow: 'visible' }}
            key={item?.GridPosition?.i}
            className="grid-item"
            data-grid={item?.GridPosition}
          >
            {!item?.SectionItemID ? (
              <div ref={menuRef}>
                <AddElementMenu
                  $positionRight={(item?.GridPosition?.x ?? 0) + (item?.GridPosition?.w ?? 1) >= 7}
                  $positionTop={(item?.GridPosition?.y ?? 0) >= 5}
                >
                  <div className="menu-items">
                    <div className="menu-item" onClick={() => handleModalType(item, 'kpi')}>
                      <span className="material-symbols-outlined">123</span>
                      <span>KPI</span>
                    </div>
                    <div className="menu-item" onClick={() => handleModalType(item, 'kpiQuery')}>
                      <span className="material-symbols-outlined">pin</span>
                      <span>KPI Query</span>
                    </div>
                    <div className="menu-item" onClick={() => handleModalType(item, 'table')}>
                      <span className="material-symbols-outlined">view_list</span>
                      <span>Table</span>
                    </div>
                    <div className="menu-item" onClick={() => handleModalType(item, 'chart')}>
                      <span className="material-symbols-outlined">insert_chart</span>
                      <span>Chart</span>
                    </div>
                    <div className="menu-item" onClick={() => handleModalType(item, 'text')}>
                      <span className="material-symbols-outlined">comment</span>
                      <span>Text</span>
                    </div>
                    <div className="menu-item" onClick={() => handleModalType(item, 'file')}>
                      <span className="material-symbols-outlined">file_upload</span>
                      <span>File</span>
                    </div>
                    <div className="menu-item" onClick={() => handleModalType(item, 'creative')}>
                      <span className="material-symbols-outlined">image</span>
                      <span>Creative</span>
                    </div>
                    <div
                      className="menu-item"
                      style={{ color: '#999', borderTop: '1px solid #eee', marginTop: '4px', paddingTop: '8px' }}
                      onClick={() => {
                        safeSectionLayoutUpdate((prevLayout) =>
                          prevLayout.map((section) => {
                            if (section.SectionID === cleanedCurrentSection.SectionID) {
                              const filteredElements = (section.SectionElements ?? []).filter(
                                (el) => el?.GridPosition?.i !== item?.GridPosition?.i,
                              );
                              return {
                                ...section,
                                SectionElements: filteredElements,
                              };
                            }
                            return section;
                          }),
                        );
                      }}
                    >
                      <span className="material-symbols-outlined">close</span>
                      <span>Cancel</span>
                    </div>
                  </div>
                </AddElementMenu>
              </div>
            ) : (
              <>
                {item?.Type === 'kpi' && (
                  <CustomReportKPI
                    sectionID={cleanedCurrentSection.SectionID}
                    deleteItem={deleteItem}
                    handleModalType={handleModalType}
                    layoutItem={item}
                    kpiValue={kpiCache[item.SectionItemID ?? '']}
                    fetchKPI={fetchKPI}
                  ></CustomReportKPI>
                )}
                {item?.Type === 'file' && (
                  <CustomReportFile
                    sectionID={cleanedCurrentSection.SectionID}
                    deleteItem={deleteItem}
                    handleModalType={handleModalType}
                    layoutItem={item}
                    selectedFile={fileCache[item.SectionItemID ?? '']}
                    fetchFile={fetchFile}
                  ></CustomReportFile>
                )}
                {item?.Type === 'text' && (
                  <CustomReportText
                    sectionID={cleanedCurrentSection.SectionID}
                    deleteItem={deleteItem}
                    handleModalType={handleModalType}
                    layoutItem={item}
                    initialText={textCache[item.SectionItemID ?? '']}
                    fetchText={fetchTextFromAPI}
                  ></CustomReportText>
                )}
                {(item?.Type === 'table' || item?.Type === 'chart' || item?.Type === 'kpiQuery') && (
                  // <div className="tableContainer" style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                  <CustomReportQuery
                    isGraph={item?.Type === 'chart' || item.Type === 'kpiQuery'}
                    sectionID={cleanedCurrentSection.SectionID}
                    deleteItem={deleteItem}
                    handleModalType={handleModalType}
                    layoutItem={item}
                    initialQuery={queryCache[item.SectionItemID ?? '']}
                    fetchQuery={fetchQuery}
                    modalType={item?.Type}
                  ></CustomReportQuery>
                  // </div>
                )}
                {item?.Type === 'creative' && (
                  <CustomReportCreative
                    key={`creative-${item.SectionItemID}-v${creativeCacheVersion}`}
                    modal={false}
                    sectionID={cleanedCurrentSection.SectionID}
                    deleteItem={deleteItem}
                    handleModalType={handleModalType}
                    layoutItem={item}
                    initialCreative={creativeCache[item.SectionItemID ?? '']}
                    fetchCreative={fetchCreative}
                  />
                )}
              </>
            )}
          </div>
        ))}
      </ReactGridLayout>

      {openedItem && !disableModal && (
        <CustomReportModalAddNew
          sectionID={cleanedCurrentSection.SectionID}
          openedItem={openedItem}
          layoutState={(cleanedCurrentSection.SectionElements ?? []).filter(
            (item): item is GridLayout => item !== null,
          )}
          // setLayoutState={setLayoutState}
          onFileSaved={handleFileSaved}
          initialFile={fileCache[openedItem.SectionItemID ?? '']}
          fetchFile={fetchFile}
          handleModalType={handleModalType}
          initialKPI={kpiCache[openedItem.SectionItemID ?? '']}
          fetchKPI={fetchKPI}
          onKpiSaved={handleKpiSaved}
          initialText={textCache[openedItem.SectionItemID ?? '']}
          fetchText={fetchTextFromAPI}
          onTextSaved={handleTextSaved}
          initialQuery={queryCache[openedItem.SectionItemID ?? '']}
          fetchQuery={fetchQuery}
          onQuerySaved={handleQuerySaved}
          initialCreative={creativeCache[openedItem.SectionItemID ?? '']}
          fetchCreative={fetchCreative}
          onCreativeSaved={handleCreativeSaved}
        ></CustomReportModalAddNew>
      )}
    </>
  );
};

export default CustomReportSection;
